import { BookOpen, Check, FileText, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { MarkdownContent } from '@/components/markdown-content'
import { cn } from '@/lib/utils'

export interface WikiBubbleState {
  operation: 'create' | 'ingest' | 'query' | 'lint'
  status: string
  detail?: string
  pages: Array<{ path: string; concept: string }>
  answer?: string
  lintReport?: string
  isRunning: boolean
  error?: string
}

interface WikiBubbleProps {
  state: WikiBubbleState
  onDismiss: () => void
}

const TITLES: Record<WikiBubbleState['operation'], string> = {
  create: 'Memory: Topic anlegen',
  ingest: 'Memory: Konzepte extrahieren',
  query: 'Memory: Frage beantworten',
  lint: 'Memory: Lint-Scan',
}

export function WikiBubble({ state, onDismiss }: WikiBubbleProps) {
  const isDone = state.status === 'done'
  const isError = state.status === 'error' || !!state.error

  return (
    <div
      className={cn(
        'rounded-lg border bg-card px-4 py-3 text-sm shadow-sm',
        isError && 'border-destructive/40',
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {state.isRunning ? (
            <Loader2 className="h-4 w-4 animate-spin text-accent" />
          ) : isError ? (
            <X className="h-4 w-4 text-destructive" />
          ) : (
            <Check className="h-4 w-4 text-brand-success" />
          )}
          <BookOpen className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold">{TITLES[state.operation]}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={onDismiss} className="h-6 px-2">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {state.detail && (
        <p className="mb-2 text-xs text-muted-foreground">
          {state.status} — {state.detail}
        </p>
      )}

      {state.pages.length > 0 && (
        <ul className="mb-2 space-y-1">
          {state.pages.map((p) => (
            <li key={p.path} className="flex items-center gap-2 font-mono text-[12px]">
              <FileText className="h-3 w-3 text-muted-foreground" />
              <span className="text-foreground">{p.concept}</span>
              <span className="text-muted-foreground">— {p.path}</span>
            </li>
          ))}
        </ul>
      )}

      {state.answer && (
        <div className="prose prose-sm dark:prose-invert max-w-none border-t border-border pt-2 text-sm">
          <MarkdownContent>{state.answer}</MarkdownContent>
        </div>
      )}

      {state.lintReport && isDone && (
        <p className="text-xs text-muted-foreground">
          Lint-Report: <span className="font-mono">{state.lintReport}</span>
        </p>
      )}

      {state.error && (
        <p className="text-xs text-destructive">{state.error}</p>
      )}
    </div>
  )
}
